// src/lib/chartingApi.js

function getLang() {
  if (typeof document !== "undefined" && document.documentElement?.lang) {
    return document.documentElement.lang;
  }
  return "ko";
}

/** 레거시: 이전 EMR 파싱 (누적/총량 & 마지막 식사탕 단계 추출) */
export function parsePreviousEMR(text) {
  const values = {
    ss: { cumulative: 0, total: 90 },  // 식사탕
    sk: { cumulative: 0, total: 90 },  // 수면킬
    zk: { cumulative: 0, total: 270 }, // 뱃살킬
    dk: { cumulative: 0, total: 90 },  // 독소킬
    lastSiktamStage: null,
  };

  const regexMap = {
    ss: /ss\s+\d+d\s*(\d+)\s*\/\s*(\d+)/,
    sk: /sk\s+\d+d\s*(\d+)\s*\/\s*(\d+)/,
    zk: /zk\s+\d+d\s*(\d+)\s*\/\s*(\d+)/,
    dk: /dk\s+\d+d\s*(\d+)\s*\/\s*(\d+)/,
  };

  const combinedText = (text || "").replace(/\s+/g, " ");
  for (const key in regexMap) {
    const m = combinedText.match(regexMap[key]);
    if (m) {
      values[key].cumulative = parseInt(m[1], 10) || 0;
      values[key].total = parseInt(m[2], 10) || values[key].total;
    }
  }

  // 첫 줄에서 식사탕 "식사탕 7-15, 8-30" 패턴의 마지막 단계 추출
  const firstLine = (text || "").split("\n")[0] || "";
  const siktamMatch = firstLine.match(/식사탕\s*([\d\s,-]+)/);
  if (siktamMatch) {
    const stagesStr = siktamMatch[1];
    const stagePairs = stagesStr.match(/\d+-\d+/g);
    if (stagePairs && stagePairs.length > 0) {
      const lastPair = stagePairs[stagePairs.length - 1];
      const lastStage = parseInt(lastPair.split("-")[0], 10);
      if (!isNaN(lastStage)) {
        values.lastSiktamStage = lastStage;
      }
    }
  }

  return values;
}

/**
 * 레거시 조합/검증 로직을 그대로 계산
 * @param {{
 *   prevEmr: string,
 *   siktam: {stage:number, days:number}[],
 *   soomyunDays: number,
 *   baetsalDose: number,
 *   baetsalDays: number,
 *   doksokillDays: number,
 *   toggles: { gonyak:boolean, chaekja:boolean, baetsalPlus:boolean, doksokillPlus:boolean },
 *   validate?: boolean
 * }} params
 * @returns {{ text: string, warnings: string[] }}
 */
export function computeChartText(params) {
  const {
    prevEmr,
    siktam,
    soomyunDays,
    baetsalDose,
    baetsalDays,
    doksokillDays,
    toggles,
    validate = false,
  } = params;

  const lang = getLang();
  const prevValues = parsePreviousEMR(prevEmr || "");
  const outputs = {
    ss: { top: null, detail: null }, // 식사탕
    sk: { top: null, detail: null }, // 수면킬
    zk: { top: null, detail: null }, // 뱃살킬
    dk: { top: null, detail: null }, // 독소킬
  };

  // 1) 식사탕: 선택한 days 합산 → 누적 + 합계, top은 "식사탕 7-15, 8-30" 처럼 days*3 반영
  let totalSiktamDays = 0;
  const siktamPrescriptions = [];
  (Array.isArray(siktam) ? siktam : []).forEach((row) => {
    const stage = Number(row.stage) || 0;
    const days = Number(row.days) || 0;
    if (stage > 0 && days > 0) {
      totalSiktamDays += days;
      siktamPrescriptions.push({ stage, days });
    }
  });

  const updatedCumulative_ss = prevValues.ss.cumulative + totalSiktamDays;
  const siktamExists = totalSiktamDays > 0 || prevValues.ss.cumulative > 0;

  if (siktamExists) {
    if (siktamPrescriptions.length > 0) {
      const details = siktamPrescriptions.map((p) => `${p.stage}-${p.days * 3}`);
        outputs.ss.top = lang === "zh-CN"
            ? process.env.REACT_APP_CN_SIKTAM_SAYAK
            : `${process.env.REACT_APP_SIKTAM_SAYAK} `;
        outputs.ss.top += details.join(", ");
    } else {
      outputs.ss.top = "식사탕0";
    }
    outputs.ss.detail = `ss ${totalSiktamDays}d\n${updatedCumulative_ss}/${prevValues.ss.total}`;
  }

  // 2) 수면킬
  const newSoomyunDays = Number(soomyunDays) || 0;
  const updatedCumulative_sk = prevValues.sk.cumulative + newSoomyunDays;
  const soomyunExists = newSoomyunDays > 0 || prevValues.sk.cumulative > 0;
  if (soomyunExists) {
    outputs.sk.top = `수${newSoomyunDays}`;
    outputs.sk.detail = `sk ${newSoomyunDays}d\n${updatedCumulative_sk}/${prevValues.sk.total}`;
  }

  // 3) 뱃살킬
  const dose = Number(baetsalDose) || 0;
  const daysZ = Number(baetsalDays) || 0;
  const calcZ = dose > 0 && daysZ > 0 ? dose * daysZ : 0;
  const updatedCumulative_zk = prevValues.zk.cumulative + calcZ;
  const baetsalExists = calcZ > 0 || prevValues.zk.cumulative > 0;
  if (baetsalExists) {
    const prefix = dose > 1 ? "#" : "";
    const label = toggles?.baetsalPlus ? "뱃+" : "뱃";
    outputs.zk.top = `${prefix}${label} ${calcZ}`;
    outputs.zk.detail = `zk ${calcZ}d\n${updatedCumulative_zk}/${prevValues.zk.total}`;
  }

  // 4) 독소킬
  const newDk = Number(doksokillDays) || 0;
  const updatedCumulative_dk = prevValues.dk.cumulative + newDk;
  const doksoExists = newDk > 0 || prevValues.dk.cumulative > 0;
  if (doksoExists) {
    const label = toggles?.doksokillPlus ? "독+" : "독";
    outputs.dk.top = `${label} ${newDk}`;
    outputs.dk.detail = `dk ${newDk}d\n${updatedCumulative_dk}/${prevValues.dk.total}`;
  }

  // 유효성 검사 (누적 > 총량)
  const warnings = [];
  if (validate) {
    if (lang === "zh-CN") {
      if (updatedCumulative_ss > prevValues.ss.total) warnings.push(`${process.env.REACT_APP_CN_JIBANG_SAYACK} (超过总 ${prevValues.ss.total}日 处方)`);
      if (updatedCumulative_sk > prevValues.sk.total) warnings.push(`${process.env.REACT_APP_CN_SUMYEON_KILL} (超过总 ${prevValues.sk.total}日 处方)`);
      if (updatedCumulative_zk > prevValues.zk.total) warnings.push(`${process.env.REACT_APP_CN_JIBANG_KILL} (超过总 ${prevValues.zk.total}日 处方)`);
      if (updatedCumulative_dk > prevValues.dk.total) warnings.push(`${process.env.REACT_APP_CN_DOKSO_KILL} (超过总 ${prevValues.dk.total}日 处方)`);
    } else {
      if (updatedCumulative_ss > prevValues.ss.total) warnings.push(`${process.env.REACT_APP_JIBANG_SAYACK} (총 ${prevValues.ss.total}일 처방 초과)`);
      if (updatedCumulative_sk > prevValues.sk.total) warnings.push(`${process.env.REACT_APP_SUMYEON_KILL} (총 ${prevValues.sk.total}일 처방 초과)`);
      if (updatedCumulative_zk > prevValues.zk.total) warnings.push(`${process.env.REACT_APP_JIBANG_KILL} (총 ${prevValues.zk.total}일 처방 초과)`);
      if (updatedCumulative_dk > prevValues.dk.total) warnings.push(`${process.env.REACT_APP_DOKSO_KILL} (총 ${prevValues.dk.total}일 처방 초과)`);
    }
  }

  // 최종 문자열 조합
  const topParts = [outputs.ss.top, outputs.sk.top, outputs.zk.top, outputs.dk.top].filter(Boolean);
  let topLine = topParts.join(" / ");

  let prefix = "";
  if (toggles?.gonyak) prefix += "곤약";
  if (toggles?.chaekja) {
    if (prefix) prefix += " ";
    prefix += "책자";
  }
  if (prefix) topLine = `${prefix} / ${topLine}`;

  const detailBlocks = [outputs.ss.detail, outputs.sk.detail, outputs.zk.detail, outputs.dk.detail].filter(Boolean);
  const text = `${topLine}\n\n${detailBlocks.join("\n\n")}`;

  return { text, warnings };
}

/** UI 레이블 (단위/옵션 라벨) */
export function labelForDay(d) {
  const lang = getLang();
  return lang === "zh-CN" ? `${d}日` : `${d}일`;
}
export function labelForStage(i) {
  const lang = getLang();
  return lang === "zh-CN" ? `${i}阶段` : `${i}단계`;
}

export const SIKTAM_DAY_OPTIONS = [5, 10, 15, 20, 25, 30];
export const SIKTAM_STAGE_MIN = 1;
export const SIKTAM_STAGE_MAX = 12;
